import {
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Space,
  Statistic,
  Table,
} from "antd";
import type { RangePickerProps } from "antd/es/date-picker";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import ContentSkeleton from "../../../components/common/ContentSkeleton";
import ErrorState from "../../../components/common/ErrorState";
import { salesApi } from "../../../services/api";
import { SalesReport } from "../../../types/sales.types";

const { RangePicker } = DatePicker;

const SalesReportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salesData, setSalesData] = useState<SalesReport[]>([]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);
  const [groupBy, setGroupBy] = useState<"dealer" | "retailer" | "none">(
    "none"
  );

  const fetchSalesReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await salesApi.getSalesReport({
        startDate: dateRange[0].format("DD-MM-YYYY"),
        endDate: dateRange[1].format("DD-MM-YYYY"),
      });

      if (response.status === "SUCCESS") {
        setSalesData(response.data);
      } else {
        setError(response.message);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch sales report";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesReport();
  }, [dateRange]);

  const disabledDate: RangePickerProps["disabledDate"] = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const getGroupedData = () => {
    if (groupBy === "none") return salesData;

    const grouped = salesData.reduce((acc, curr) => {
      const key = groupBy === "dealer" ? curr.dealerName : curr.retailerName;
      if (!acc[key]) {
        acc[key] = {
          key,
          name: key,
          totalTokens: 0,
          totalAmount: 0,
          transactions: 0,
        };
      }
      acc[key].totalTokens += curr.unitsSold;
      acc[key].totalAmount += curr.paidAmount;
      acc[key].transactions += 1;
      return acc;
    }, {} as Record<string, any>);

    return Object.values(grouped);
  };

  const getColumns = () => {
    if (groupBy === "none") {
      return [
        {
          title: "Date",
          dataIndex: "transactionDate",
          key: "transactionDate",
          render: (date: string) =>
            dayjs(date, "DD-MM-YYYY").format("DD MMM YYYY"),
        },
        {
          title: "Dealer",
          dataIndex: "dealerName",
          key: "dealerName",
        },
        {
          title: "Retailer",
          dataIndex: "retailerName",
          key: "retailerName",
        },
        {
          title: "Tokens",
          dataIndex: "numberOfTokens",
          key: "numberOfTokens",
        },
        {
          title: "Amount",
          dataIndex: "paidAmount",
          key: "paidAmount",
          render: (amount: number) => `৳${amount.toLocaleString()}`,
        },
        {
          title: "Payment Method",
          dataIndex: "paymentMethod",
          key: "paymentMethod",
        },
        {
          title: "Transaction ID",
          dataIndex: "transactionId",
          key: "transactionId",
        },
      ];
    }

    return [
      {
        title: groupBy === "dealer" ? "Dealer" : "Retailer",
        dataIndex: "name",
        key: "name",
      },
      {
        title: "Total Tokens",
        dataIndex: "totalTokens",
        key: "totalTokens",
      },
      {
        title: "Total Amount",
        dataIndex: "totalAmount",
        key: "totalAmount",
        render: (amount: number) => `৳${amount.toLocaleString()}`,
      },
      {
        title: "Transactions",
        dataIndex: "transactions",
        key: "transactions",
      },
    ];
  };

  const getTotalStats = () => {
    const totals = salesData.reduce(
      (acc, curr) => {
        acc.tokens += curr.unitsSold;
        acc.amount += curr.paidAmount;
        return acc;
      },
      { tokens: 0, amount: 0 }
    );

    return (
      <Row gutter={16}>
        <Col span={8}>
          <Statistic title="Total Tokens Sold" value={totals.tokens} />
        </Col>
        <Col span={8}>
          <Statistic title="Total Amount" value={totals.amount} prefix="৳" />
        </Col>
        <Col span={8}>
          <Statistic title="Total Transactions" value={salesData.length} />
        </Col>
      </Row>
    );
  };

  if (error) {
    return <ErrorState error={error} onRetry={fetchSalesReport} />;
  }

  return (
    <Card title="Sales Report">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Row gutter={16} align="middle">
          <Col>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              disabledDate={disabledDate}
              format="DD-MM-YYYY"
            />
          </Col>
          <Col>
            <Select
              value={groupBy}
              onChange={setGroupBy}
              style={{ width: 150 }}
            >
              <Select.Option value="none">No Grouping</Select.Option>
              <Select.Option value="dealer">Group by Dealer</Select.Option>
              <Select.Option value="retailer">Group by Retailer</Select.Option>
            </Select>
          </Col>
          <Col>
            <Button onClick={fetchSalesReport}>Refresh</Button>
          </Col>
        </Row>

        {getTotalStats()}

        <Table
          columns={getColumns()}
          dataSource={getGroupedData()}
          loading={loading}
          rowKey={groupBy === "none" ? "id" : "name"}
          pagination={{
            total: getGroupedData().length,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} records`,
          }}
          scroll={{ x: 800 }}
        />
      </Space>
    </Card>
  );
};

export default SalesReportPage;
